import { useEffect, useState, useCallback } from "react";
import api from "../api/api";
import SideNavLayout from "../customsidenav/sidenavlayout";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import { getPracticeId, practiceShortName } from "../config/practice";

/*
 * OSA's call log (/logs for features.vapiCallLogs, 7825), ported from its own
 * build (logs/log.jsx): the practice's Vapi receptionist calls for a date
 * range -- caller (patient name when the number matches a chart), summary,
 * call time, duration -- with a phone/name search over the page on screen.
 *
 * v1 GET /vapi/calls, pinned to v1 (`_gw`) as OSA calls it. OSA's bundle sent
 * its Vapi org and assistant ids from the page; this page sends neither: the
 * server reads the practice's Vapi credentials, org and assistant from its own
 * config (practices.practiceconfig / env), so no id ships in the UI bundle.
 */

// The request for one page of calls (MUI pages are 0-based, the API's 1-based).
export function vapiCallsRequest(practiseid, { startDate, endDate, page, rowsPerPage }) {
  return [
    `/v1/${practiseid}/vapi/calls`,
    { params: { startDate, endDate, page: page + 1, limit: rowsPerPage }, _gw: "v1" },
  ];
}

export const formatDuration = (startedAt, endedAt) => {
  if (!startedAt || !endedAt) return "";
  const secs = Math.round((new Date(endedAt) - new Date(startedAt)) / 1000);
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
};

const getPhone = (item) => item.phone_number || "—";
const getPatientName = (item) => item.patient_name || "—";
const getSummary = (item) => item.summary || "—";

const ERROR_TEXT = "Something went wrong while fetching logs. Please try again.";

const VapiCallLogs = () => {
  const practiseid = getPracticeId();

  const [allLogs, setAllLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [startDate, setStartDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const fetchData = useCallback(
    async (pageOverride) => {
      try {
        setLoading(true);
        setError(null);
        const currentPage = pageOverride !== undefined ? pageOverride : page;
        const res = await api.get(
          ...vapiCallsRequest(practiseid, { startDate, endDate, page: currentPage, rowsPerPage }),
        );
        setAllLogs(Array.isArray(res?.data) ? res.data : []);
        setTotalCount(res?.total_records ?? 0);
      } catch (err) {
        console.error(err);
        setError(ERROR_TEXT);
        setAllLogs([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [practiseid, page, rowsPerPage, startDate, endDate],
  );

  useEffect(() => {
    document.title = `${practiceShortName()} - Logs`;
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetFilters = () => {
    setStartDate(dayjs().format("YYYY-MM-DD"));
    setEndDate(dayjs().format("YYYY-MM-DD"));
    setSearch("");
    setPage(0);
  };

  // Search filters the page on screen (as OSA).
  const displayRows = search.trim()
    ? allLogs.filter((item) => {
        const q = search.trim().toLowerCase();
        return getPhone(item).toLowerCase().includes(q) || getPatientName(item).toLowerCase().includes(q);
      })
    : allLogs;

  return (
    <SideNavLayout>
      <div className="w-full">
        {/* Filters Row */}
        <div className="w-full flex items-center justify-between gap-4 mb-3 font-inter">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3.5 py-2 shadow-2xs">
              <span className="text-xs font-semibold text-slate-500 font-inter whitespace-nowrap">Start Date:</span>
              <input
                type="date"
                aria-label="Start Date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setPage(0);
                }}
                className="bg-transparent outline-none text-xs text-slate-800 font-inter font-medium cursor-pointer"
              />
            </div>
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3.5 py-2 shadow-2xs">
              <span className="text-xs font-semibold text-slate-500 font-inter whitespace-nowrap">End Date:</span>
              <input
                type="date"
                aria-label="End Date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setPage(0);
                }}
                className="bg-transparent outline-none text-xs text-slate-800 font-inter font-medium cursor-pointer"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative w-64">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs"
              />
              <input
                type="text"
                placeholder="Search phone or name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-xs font-inter bg-white border border-slate-200 rounded-xl outline-none focus:border-[#1D2951] shadow-2xs placeholder:text-slate-400"
              />
            </div>
            <button
              onClick={resetFilters}
              className="px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold font-inter transition-all cursor-pointer shadow-2xs whitespace-nowrap active:scale-[0.98]"
            >
              RESET FILTERS
            </button>
            <button
              onClick={() => fetchData()}
              className="px-4 py-2 rounded-xl bg-[#1D2951] hover:bg-[#162040] text-white text-xs font-semibold font-inter shadow-2xs transition-all cursor-pointer flex items-center gap-1.5 whitespace-nowrap active:scale-[0.98]"
            >
              <RefreshIcon sx={{ fontSize: 16 }} />
              SYNC NOW
            </button>
          </div>
        </div>

        <Paper
          elevation={0}
          sx={{
            width: "100%",
            overflow: "hidden",
            borderRadius: "16px",
            border: "1px solid #E2E8F0",
            boxShadow: "none",
          }}
        >
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalCount || displayRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
          <TableContainer sx={{ maxHeight: "75vh" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap", width: 180, color: "#475569", py: 1.5 }}>
                    Patient / Caller
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475569", py: 1.5 }}>Summary</TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap", width: 200, color: "#475569", py: 1.5 }}>
                    Call Time
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 600, whiteSpace: "nowrap", width: 90, color: "#475569", py: 1.5 }}
                  >
                    Duration
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6, color: "#64748B" }}>
                      {error ? error : "No call logs found."}
                    </TableCell>
                  </TableRow>
                ) : (
                  displayRows.map((item, idx) => {
                    const name = getPatientName(item);
                    const phone = getPhone(item);
                    const hasName = name && name !== "—" && name !== phone;
                    return (
                      <TableRow key={item.call_id || idx} hover sx={{ "& > td": { verticalAlign: "top", py: 1.25 } }}>
                        <TableCell sx={{ whiteSpace: "nowrap", width: 180 }}>
                          <div>
                            <div className="text-xs font-semibold text-slate-800 font-inter">
                              {hasName ? name : phone}
                            </div>
                            {hasName && phone && phone !== "—" && (
                              <div className="text-[11px] text-slate-400 font-inter mt-0.5">{phone}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell sx={{ pr: 3 }}>
                          <div className="leading-relaxed text-xs text-slate-600 font-inter whitespace-normal">
                            {getSummary(item)}
                          </div>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", width: 200 }}>
                          {item.started_at ? (
                            <div>
                              <div className="text-xs font-semibold text-slate-800 font-inter">
                                {dayjs(item.started_at).format("MMM D, YYYY")}
                              </div>
                              <div className="text-[11px] text-slate-400 font-inter mt-0.5 flex items-center gap-1">
                                <span>{dayjs(item.started_at).format("h:mm:ss A")}</span>
                                {item.ended_at && (
                                  <>
                                    <span className="text-slate-300">–</span>
                                    <span>{dayjs(item.ended_at).format("h:mm:ss A")}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: "nowrap", width: 90 }}>
                          {item.started_at && item.ended_at ? (
                            <span className="px-2 py-0.5 text-xs font-semibold bg-slate-100 text-slate-700 rounded-md font-inter inline-block">
                              {formatDuration(item.started_at, item.ended_at)}
                            </span>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </div>

      <Backdrop open={loading} sx={{ color: "#fff", zIndex: 9999 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </SideNavLayout>
  );
};

export default VapiCallLogs;
